import { Controller } from '@hotwired/stimulus'

// Accept and Sign section on the public proposal page
// (Sales::ProposalDocument::SignatureComponent). The customer either types their
// name (previewed live in a script font) or draws on the canvas pad; both are
// rendered to a PNG data URL at submit time so the server always stores one
// uniform signature image. The submit button stays disabled until a payment
// method is captured (Sales::ProposalDocument::PaymentComponent /
// sales_payment_controller.js), which this controller hears about via the
// `sales-payment:captured` event dispatched on `document` -- no DOM reaching
// across sections. The optional-add-on picks live outside this <form>
// entirely (Sales::ProposalDocument::OptionalComponent, wired to
// sales-proposal-totals), so they are read from the document and injected as
// hidden fields right before submit. The server re-validates the signature,
// the consent, and every accepted legal version regardless. ISSUE-3823.
export default class extends Controller {
  static targets = [
    'name',
    'typeTab',
    'drawTab',
    'typePanel',
    'drawPanel',
    'typedInput',
    'typedPreview',
    'canvas',
    'consent',
    'kindField',
    'typedField',
    'dataField',
    'timezoneField',
    'selections',
    'error',
    'submitButton',
    'submitHint',
    'declineForm'
  ]

  static values = { scriptFont: String }

  connect() {
    this.kind = 'typed'
    this.hasDrawn = false
    this.typedDirty = false
    try {
      this.timezoneFieldTarget.value = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    } catch {
      this.timezoneFieldTarget.value = ''
    }
    this.setupCanvas()
  }

  disconnect() {
    this.padListeners?.abort()
  }

  // ── Payment gate ─────────────────────────────────────────────────────────

  enableSubmit() {
    this.submitButtonTarget.disabled = false
    if (this.hasSubmitHintTarget) this.submitHintTarget.classList.add('d-none')
  }

  // ── Typed signature ──────────────────────────────────────────────────────

  nameChanged() {
    // Until the customer customizes the signature text, follow the name field
    // so most signers never have to type twice.
    if (this.typedDirty) return

    this.typedInputTarget.value = this.nameTarget.value
    this.renderTypedPreview()
  }

  typedChanged() {
    this.typedDirty = true
    this.renderTypedPreview()
  }

  renderTypedPreview() {
    this.typedPreviewTarget.textContent = this.typedInputTarget.value
  }

  showType(event) {
    event.preventDefault()
    this.kind = 'typed'
    this.typePanelTarget.classList.remove('d-none')
    this.drawPanelTarget.classList.add('d-none')
    this.swapTabStyles(this.typeTabTarget, this.drawTabTarget)
  }

  showDraw(event) {
    event.preventDefault()
    this.kind = 'drawn'
    this.typePanelTarget.classList.add('d-none')
    this.drawPanelTarget.classList.remove('d-none')
    this.swapTabStyles(this.drawTabTarget, this.typeTabTarget)
    this.resizePad()
  }

  swapTabStyles(active, inactive) {
    active.classList.replace('btn-outline-secondary', 'btn-outline-primary')
    inactive.classList.replace('btn-outline-primary', 'btn-outline-secondary')
  }

  // ── Drawn signature (minimal pointer-events pad) ─────────────────────────

  setupCanvas() {
    const canvas = this.canvasTarget

    // All pad listeners hang off one AbortController so a re-run of connect()
    // detaches the previous set instead of stacking duplicates.
    this.padListeners?.abort()
    this.padListeners = new AbortController()
    const { signal } = this.padListeners

    canvas.addEventListener('pointerdown', (e) => {
      e.preventDefault()
      canvas.setPointerCapture(e.pointerId)
      this.drawing = true
      const point = this.canvasPoint(e)
      this.ctx.beginPath()
      this.ctx.moveTo(point.x, point.y)
    }, { signal })
    canvas.addEventListener('pointermove', (e) => {
      if (!this.drawing) return

      // A tap with no movement must not count as a signature: hasDrawn only
      // flips once a segment is actually stroked, not on pointerdown.
      this.hasDrawn = true
      const point = this.canvasPoint(e)
      this.ctx.lineTo(point.x, point.y)
      this.ctx.stroke()
    }, { signal })
    const stop = () => { this.drawing = false }
    canvas.addEventListener('pointerup', stop, { signal })
    canvas.addEventListener('pointercancel', stop, { signal })

    this.drawing = false
    this.resizePad()
  }

  resizePad() {
    const canvas = this.canvasTarget
    const wrap = canvas.parentElement
    const ratio = window.devicePixelRatio || 1
    const fallbackWidth = parseInt(canvas.dataset.baseWidth || '420', 10)
    const fallbackHeight = parseInt(canvas.dataset.baseHeight || '120', 10)
    const cssWidth = wrap.clientWidth || fallbackWidth
    const cssHeight = fallbackHeight

    canvas.style.width = wrap.clientWidth ? '100%' : `${fallbackWidth}px`
    canvas.style.height = `${cssHeight}px`

    if (this.baseWidth === cssWidth && this.baseHeight === cssHeight && this.ctx) return

    this.baseWidth = cssWidth
    this.baseHeight = cssHeight
    canvas.width = cssWidth * ratio
    canvas.height = cssHeight * ratio

    const ctx = canvas.getContext('2d')
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.scale(ratio, ratio)
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#1f2933'
    this.ctx = ctx
    this.hasDrawn = false
  }

  // Scale pointer position from rendered CSS pixels into the pad's drawing
  // space so ink tracks the cursor when the bitmap and layout differ.
  canvasPoint(event) {
    const rect = this.canvasTarget.getBoundingClientRect()
    return {
      x: (event.clientX - rect.left) * (this.baseWidth / rect.width),
      y: (event.clientY - rect.top) * (this.baseHeight / rect.height)
    }
  }

  clearCanvas(event) {
    event.preventDefault()
    const canvas = this.canvasTarget
    this.ctx.clearRect(0, 0, canvas.width, canvas.height)
    this.hasDrawn = false
  }

  // ── Decline ──────────────────────────────────────────────────────────────

  toggleDecline(event) {
    event.preventDefault()
    this.declineFormTarget.classList.toggle('d-none')
  }

  confirmDecline(event) {
    if (!window.confirm('Decline this proposal? This cannot be undone.')) {
      event.preventDefault()
    }
  }

  // ── Submit ───────────────────────────────────────────────────────────────

  submit(event) {
    if (!this.consentTarget.checked) {
      event.preventDefault()
      this.showError('Please agree to the terms before signing.')
      return
    }

    const dataUrl = this.signatureDataUrl()
    if (!dataUrl) {
      event.preventDefault()
      this.showError(this.kind === 'typed'
        ? 'Please type your signature before submitting.'
        : 'Please draw your signature before submitting.')
      return
    }

    this.kindFieldTarget.value = this.kind
    this.typedFieldTarget.value = this.kind === 'typed' ? this.typedInputTarget.value.trim() : ''
    this.dataFieldTarget.value = dataUrl
    this.injectOptionalSelections()

    this.errorTarget.hidden = true
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.textContent = 'Recording your signature...'
  }

  signatureDataUrl() {
    if (this.kind === 'drawn') {
      return this.hasDrawn ? this.canvasTarget.toDataURL('image/png') : null
    }

    const text = this.typedInputTarget.value.trim()
    if (!text) return null

    return this.renderTypedToDataUrl(text)
  }

  // Draws the typed name onto an offscreen canvas in the script font,
  // shrinking the font until long names fit, so typed and drawn signatures
  // both submit as a PNG.
  renderTypedToDataUrl(text) {
    const canvas = document.createElement('canvas')
    canvas.width = 420
    canvas.height = 120
    const ctx = canvas.getContext('2d')
    ctx.fillStyle = '#1f2933'
    ctx.textBaseline = 'middle'

    let size = 48
    do {
      ctx.font = `${size}px ${this.scriptFontValue}`
      size -= 2
    } while (size > 14 && ctx.measureText(text).width > canvas.width - 24)

    ctx.fillText(text, 12, canvas.height / 2)
    return canvas.toDataURL('image/png')
  }

  // The optional-add-on picks live outside this <form> (OptionalComponent /
  // sales-proposal-totals) -- read the checked toggles from the document and
  // submit them alongside the signature.
  injectOptionalSelections() {
    this.selectionsTarget.innerHTML = ''
    const checked = document.querySelectorAll('[data-sales-proposal-totals-target="toggle"]:checked')
    checked.forEach((box) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'signature[included_optional_ids][]'
      input.value = box.value
      this.selectionsTarget.appendChild(input)
    })
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }
}
